// The single source of truth for every selectable font across the app — the editor font
// picker, the brand-font resolver, and the AI generation prompt all derive from FONT_CATALOG.
// Google families are auto-generated (scripts/genFontCatalog.mjs); Fontshare's premium set is
// curated below. Nothing here is loaded eagerly — see loadFont.ts for on-demand fetching.
import { GOOGLE_FONTS_RAW } from './catalog.data'

export type FontCategory = 'sans-serif' | 'serif' | 'display' | 'handwriting' | 'monospace'
export type FontSource = 'google' | 'fontshare'

export interface FontMeta {
  /** Stable kebab-case id used as the picker value and stored on elements' font ids. */
  id: string
  /** Human label shown in the picker (also the CSS family name). */
  label: string
  /** CSS font-family token (already quoted if it contains spaces). */
  family: string
  category: FontCategory
  /** Numeric weights the font's files actually ship — request only these to avoid faux-bold. */
  weights: number[]
  hasItalic: boolean
  source: FontSource
}

export const slugifyFont = (family: string) =>
  family
    .trim()
    .toLowerCase()
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')

/** Wrap multi-word families in quotes so the value is a valid CSS font-family token. */
const cssFamily = (family: string) => (/\s/.test(family) ? `'${family}'` : family)

/** Fontshare's premium, commercially-free faces — the "doesn't look free" tier. Curated by hand;
 *  Fontshare has no metadata endpoint as clean as Google's. Weights are the common shipped set. */
const FONTSHARE_FAMILIES: { family: string; category: FontCategory; weights?: number[] }[] = [
  { family: 'Satoshi', category: 'sans-serif' },
  { family: 'General Sans', category: 'sans-serif' },
  { family: 'Switzer', category: 'sans-serif' },
  { family: 'Supreme', category: 'sans-serif' },
  { family: 'Synonym', category: 'sans-serif' },
  { family: 'Clash Grotesk', category: 'sans-serif' },
  { family: 'Clash Display', category: 'display' },
  { family: 'Cabinet Grotesk', category: 'display' },
  { family: 'Chillax', category: 'display' },
  { family: 'Ranade', category: 'display' },
  { family: 'Telma', category: 'display' },
  { family: 'Author', category: 'serif' },
  { family: 'Sentient', category: 'serif' },
  { family: 'Zodiak', category: 'serif' },
  { family: 'Boska', category: 'serif' },
  { family: 'Erode', category: 'serif' },
  { family: 'Bespoke Serif', category: 'serif' },
  { family: 'Bespoke Sans', category: 'sans-serif' },
  { family: 'Tabular', category: 'monospace' },
]

const fontshareMeta: FontMeta[] = FONTSHARE_FAMILIES.map((f) => ({
  id: slugifyFont(f.family),
  label: f.family,
  family: cssFamily(f.family),
  category: f.category,
  weights: f.weights ?? [300, 400, 500, 600, 700],
  hasItalic: false,
  source: 'fontshare',
}))

const googleMeta: FontMeta[] = GOOGLE_FONTS_RAW.map(([family, category, weights, italic]) => ({
  id: slugifyFont(family),
  label: family,
  family: cssFamily(family),
  category,
  weights,
  hasItalic: italic === 1,
  source: 'google',
}))

/** Fontshare first (curated premium), then Google by popularity. De-duped by id (Fontshare wins). */
export const FONT_CATALOG: FontMeta[] = (() => {
  const seen = new Set<string>()
  const out: FontMeta[] = []
  for (const f of [...fontshareMeta, ...googleMeta]) {
    if (seen.has(f.id)) continue
    seen.add(f.id)
    out.push(f)
  }
  return out
})()

const BY_ID = new Map(FONT_CATALOG.map((f) => [f.id, f]))
const BY_FAMILY = new Map(FONT_CATALOG.map((f) => [f.family.replace(/['"]/g, '').toLowerCase(), f]))
const INDEX_BY_ID = new Map(FONT_CATALOG.map((f, i) => [f.id, i]))

export const getFontById = (id?: string | null): FontMeta | undefined =>
  id ? BY_ID.get(id) : undefined

/** Position of a font in the full (unfiltered) catalog — used by the picker to scroll to the
 *  current selection on open. -1 if not found. */
export const fontIndexById = (id?: string | null): number => (id ? INDEX_BY_ID.get(id) ?? -1 : -1)

/** Resolve a stored CSS font-family string (e.g. "'Open Sans', sans-serif") back to a catalog entry. */
export const getFontByFamily = (family?: string | null): FontMeta | undefined => {
  if (!family) return undefined
  const lead = family.split(',')[0].replace(/['"]/g, '').trim().toLowerCase()
  return BY_FAMILY.get(lead)
}

/** Fonts loaded eagerly in root.tsx for instant first paint — keep this list short. */
export const DEFAULT_FONT_IDS = [
  'inter',
  'roboto',
  'poppins',
  'montserrat',
  'open-sans',
  'playfair-display',
] as const

export type FontCategoryFilter = FontCategory | 'all'
export type FontSourceFilter = FontSource | 'all'

/** Filter the full catalog by name, category, and source — returns ALL matches (the picker windows
 *  them). Results stay popularity-sorted because the catalog is. */
export function filterFonts(opts: {
  query?: string
  category?: FontCategoryFilter
  source?: FontSourceFilter
}): FontMeta[] {
  const q = (opts.query ?? '').trim().toLowerCase()
  const cat = opts.category ?? 'all'
  const src = opts.source ?? 'all'
  return FONT_CATALOG.filter(
    (f) =>
      (cat === 'all' || f.category === cat) &&
      (src === 'all' || f.source === src) &&
      (!q || f.label.toLowerCase().includes(q)),
  )
}
